"use client"

import * as React from "react"

import { cn } from "@/lib/utils"

interface TableProps extends React.ComponentProps<"table"> {
  containerClassName?: string;
  containerRef?: React.Ref<HTMLDivElement>;
}

function Table({ className, containerClassName, containerRef, ...props }: TableProps) {
  return (
    <div
      ref={containerRef}
      data-slot="table-container"
      className={cn("relative w-full overflow-x-auto", containerClassName)}
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  )
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn("[&_tr]:border-b", className)}
      {...props}
    />
  )
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  )
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "bg-muted/50 border-t font-medium [&>tr]:last:border-b-0",
        className
      )}
      {...props}
    />
  )
}

function TableRow({ className, ...props }: React.ComponentProps<"tr">) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        "hover:bg-muted/50 data-[state=selected]:bg-muted border-b transition-colors",
        className
      )}
      {...props}
    />
  )
}

function TableHead({ className, ...props }: React.ComponentProps<"th">) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        "text-foreground h-10 px-2 text-left align-middle font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        className
      )}
      {...props}
    />
  )
}

interface TableCellEditingProps {
  editing?: boolean;
  editable?: boolean;
  editValue?: string;
  onEditSave?: (value: string) => void;
  onEditCancel?: () => void;
}

function TableCell({ className, editing, editable, editValue, onEditSave, onEditCancel, ...props }: React.ComponentProps<"td"> & TableCellEditingProps) {
  const cellRef = React.useRef<HTMLTableCellElement>(null);

  React.useEffect(() => {
    if (editing && cellRef.current) {
      cellRef.current.textContent = editValue ?? '';
      cellRef.current.focus();
      const range = document.createRange();
      range.selectNodeContents(cellRef.current);
      const selection = window.getSelection();
      selection?.removeAllRanges();
      selection?.addRange(range);
    }
  }, [editing]);

  if (editing) {
    return (
      <td
        ref={cellRef}
        data-slot="table-cell"
        contentEditable
        suppressContentEditableWarning
        className={cn(
          "p-2 align-middle whitespace-nowrap outline-none ring-1 ring-blue-400 rounded",
          className
        )}
        onBlur={(e) => {
          const value = (e.target as HTMLElement).innerText;
          onEditSave?.(value);
        }}
        onKeyDown={(e) => {
          if (e.key === 'Escape') {
            e.preventDefault();
            onEditCancel?.();
          } else if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            const value = cellRef.current?.innerText ?? '';
            onEditSave?.(value);
          }
        }}
        {...props}
      />
    );
  }

  return (
    <td
      data-slot="table-cell"
      className={cn(
        "p-2 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 [&>[role=checkbox]]:translate-y-[2px]",
        editable && !editing && 'cursor-pointer',
        className
      )}
      {...props}
    >
      {props.children}
    </td>
  )
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  )
}

export {
  Table,
  TableHeader,
  TableBody,
  TableFooter,
  TableHead,
  TableRow,
  TableCell,
  TableCaption,
}
